import { useEffect, useRef, useState, type PointerEvent } from 'react';

type Pt = { x: number; y: number };
export type PhotoView = { scale: number; x: number; y: number };

const SWIPE_PX = 48;
const MIN_SCALE = 1;
const MAX_SCALE = 3.5;
const MIN_PINCH = 16;

function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function clamp(n: number, lo: number, hi: number): number {
  if (!Number.isFinite(n)) return lo;
  return Math.min(hi, Math.max(lo, n));
}

function saneView(v: PhotoView): PhotoView {
  const scale = clamp(v.scale, MIN_SCALE, MAX_SCALE);
  const limit = 480 * (scale - 1);
  if (scale <= 1.02) return { scale: 1, x: 0, y: 0 };
  return {
    scale,
    x: clamp(v.x, -limit, limit),
    y: clamp(v.y, -limit, limit),
  };
}

export function usePhotoGestures(index: number, count: number, onIndex: (i: number) => void) {
  const [view, setView] = useState<PhotoView>({ scale: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const pts = useRef(new Map<number, Pt>());
  const pinch = useRef<{ dist: number; scale: number } | null>(null);
  const pan = useRef<{ x: number; y: number; tx: number; ty: number } | null>(null);
  const swipe = useRef<Pt | null>(null);
  const pinched = useRef(false);

  useEffect(() => {
    setView({ scale: 1, x: 0, y: 0 });
    pts.current.clear();
    pinch.current = null;
    pan.current = null;
    swipe.current = null;
    pinched.current = false;
  }, [index]);

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* some mobile browsers reject capture mid-gesture */
    }
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const all = [...pts.current.values()];
    if (all.length >= 2) {
      const span = dist(all[0], all[1]);
      pinched.current = true;
      pan.current = null;
      swipe.current = null;
      pinch.current = span >= MIN_PINCH ? { dist: span, scale: viewRef.current.scale } : null;
      return;
    }
    if (viewRef.current.scale > 1.05) {
      pan.current = {
        x: e.clientX,
        y: e.clientY,
        tx: viewRef.current.x,
        ty: viewRef.current.y,
      };
      swipe.current = null;
    } else {
      swipe.current = { x: e.clientX, y: e.clientY };
      pan.current = null;
    }
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    if (!pts.current.has(e.pointerId)) return;
    pts.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const all = [...pts.current.values()];
    const origin = pinch.current;
    if (all.length >= 2 && origin && origin.dist >= MIN_PINCH) {
      const ratio = dist(all[0], all[1]) / origin.dist;
      const scale = clamp(origin.scale * ratio, MIN_SCALE, MAX_SCALE);
      setView((v) => saneView({ ...v, scale }));
      return;
    }
    const drag = pan.current;
    if (all.length === 1 && drag && viewRef.current.scale > 1.05) {
      setView(
        saneView({
          scale: viewRef.current.scale,
          x: drag.tx + (e.clientX - drag.x),
          y: drag.ty + (e.clientY - drag.y),
        }),
      );
    }
  }

  function endPointer(e: PointerEvent<HTMLDivElement>) {
    const swipeOrigin = swipe.current;
    pts.current.delete(e.pointerId);
    if (pts.current.size < 2) pinch.current = null;

    if (pts.current.size > 0) return;

    pan.current = null;
    swipe.current = null;
    setView((v) => saneView(v));

    const current = viewRef.current;
    if (pinched.current) {
      pinched.current = false;
      return;
    }
    if (!swipeOrigin || count < 2 || current.scale > 1.05) return;
    const dx = e.clientX - swipeOrigin.x;
    const dy = e.clientY - swipeOrigin.y;
    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) onIndex((index + 1) % count);
    else onIndex((index - 1 + count) % count);
  }

  return {
    view: saneView(view),
    stageProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endPointer,
      onPointerCancel: endPointer,
    },
  };
}
